import { Component, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { BookingService } from '../services/booking.service'
import { BookingDetails } from '../domain/BookingDetails'
import { Subject, takeUntil, mergeMap, switchMap, of } from 'rxjs'
import { BookingDocket, CoverMenuChoice } from '../domain/BookingDocket'
import { ProductLibrary } from '../domain/ProductLibrary'
import { ProductLibraryService } from '../services/product-library.service'
import { Menu } from '../domain/Consumable'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { MenuViewComponent } from '../menu-choices-edit/menu-view.component'
import { Venue } from '../domain/Venue'
import { VenueService } from '../services/venue.service'
import { UniqueNameInputComponent } from './unique-name-input.component'

@Component({
    selector: 'app-individual-menu-choices',
    templateUrl: './individual-menu-choices.component.html',
    styleUrls: ['./individual-menu-choices.component.sass'],
})
export class IndividualMenuChoicesComponent implements OnInit, OnDestroy {
    bookingDetails: BookingDetails | null = null
    venue: Venue | null = null
    partySizePluralMapping = {
        '=1': '1 person',
        other: '# people',
    }
    docket: BookingDocket | null = null
    productLibrary: ProductLibrary | null = null
    menu: Menu | null = null
    submittedName: string | null = null
    private onDestroy$ = new Subject<boolean>()

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private bookingService: BookingService,
        private productLibraryService: ProductLibraryService,
        private venueService: VenueService,
        private modalService: NgbModal
    ) {
    }

    ngOnInit() {
        this.route.params.pipe(
            takeUntil(this.onDestroy$),
            mergeMap(params => this.bookingService.getBookingDetails(params['bookingId'])),
            switchMap(details => {
                this.bookingDetails = details
                this.docket = details.docket
                if (!details) {
                    return of(null)
                }
                return this.venueService.getWithId(details.venueId)
            }),
            switchMap(venue => {
                this.venue = venue
                if (!this.bookingDetails) {
                    return of(null)
                }
                return this.productLibraryService.getForVenueId(this.bookingDetails.venueId)
            })
        ).subscribe(productLibrary => {
            this.productLibrary = productLibrary
            this.menu = this.resolveMenu()
        })
        
        this.route.queryParams.pipe(
            takeUntil(this.onDestroy$)
        ).subscribe(params => {
            this.submittedName = params['name'] || null
        })
    }

    ngOnDestroy() {
        this.onDestroy$.next(true)
        this.onDestroy$.complete()
    }

    private resolveMenu(): Menu | null {
        if (!this.productLibrary || !this.docket) {
            return null
        }
        return this.productLibrary.getMenu(this.docket.menuId)
    }

    openMenuView() {
        if (!this.menu || !this.venue || !this.productLibrary) {
            return
        }
        const modalRef = this.modalService.open(MenuViewComponent, { size: 'xl', fullscreen: 'md', scrollable: true, windowClass: 'customer-menu-window', modalDialogClass: 'modal-fluid-until-md', ariaLabelledBy: 'customer-menu-title' })
        const component = modalRef.componentInstance as MenuViewComponent
        component.venue = this.venue
        component.menu = this.menu
        component.productLibrary = this.productLibrary
        component.docket = this.docket
    }

    addMyChoices() {
        if (!this.menu || !this.venue || !this.productLibrary || !this.docket || !this.bookingDetails) {
            return
        }

        const nameModalRef = this.modalService.open(UniqueNameInputComponent, {
            backdrop: 'static',
        })
        const nameComponent = nameModalRef.componentInstance as UniqueNameInputComponent
        nameComponent.venue = this.venue
        nameComponent.existingNames = this.getExistingNames()
        nameComponent.duplicateErrorMessage = 'Someone has already added menu selections under that name. Please use a different name.'

        nameModalRef.result.then((name: string) => {
            // Keep the name on the summary history entry so Back and a successful save return to this guest.
            this.router.navigate([], {
                relativeTo: this.route, queryParams: { name }, queryParamsHandling: 'merge', replaceUrl: true
            }).then(() => this.router.navigate(['choices'], {
                relativeTo: this.route, queryParams: { name }, state: { menuChoicesEntry: true }
            }))
        }).catch(() => {
        })
    }

    private getExistingNames(): string[] {
        if (!this.docket || !this.docket.coverChoices) {
            return []
        }
        return this.docket.coverChoices
            .map(choice => choice?.name)
            .filter((name): name is string => !!name && name.trim().length > 0)
    }

    hasCurrentUserSubmittedChoices(): boolean {
        if (!this.submittedName || !this.docket) {
            return false
        }
        const normalizedSubmittedName = this.submittedName.trim().toLowerCase()
        return this.docket.coverChoices.some(choice => 
            choice?.name?.toLowerCase() === normalizedSubmittedName &&
            choice.selections &&
            choice.selections.length > 0
        )
    }

    get canAddMoreChoices(): boolean {
        if (!this.docket || !this.bookingDetails) {
            return true
        }
        const choicesWithSelections = this.docket.coverChoices.filter(
            c => c && c.selections && c.selections.length > 0
        ).length
        return choicesWithSelections < this.bookingDetails.partySize
    }

    get allChoicesEntered(): boolean {
        if (!this.docket || !this.bookingDetails) {
            return false
        }
        return this.docket.isComplete(this.bookingDetails.partySize)
    }

    get currentUserChoices(): { productName: string, modifierNames: string | null }[] {
        if (!this.submittedName || !this.docket || !this.productLibrary || !this.menu) {
            return []
        }
        
        const normalizedSubmittedName = this.submittedName.trim().toLowerCase()
        const coverIndex = this.docket.coverChoices.findIndex(choice => 
            choice?.name?.toLowerCase() === normalizedSubmittedName
        )
        
        if (coverIndex === -1) {
            return []
        }
        
        return this.computeViewsFromIndex(coverIndex)
    }

    private computeViewsFromIndex(index: number): { productName: string, modifierNames: string | null }[] {
        if (!this.productLibrary || !this.menu || !this.docket) {
            return []
        }
        const existingChoices: CoverMenuChoice[] = this.docket.coverChoices || []
        const choice = existingChoices[index] || new CoverMenuChoice(null, null, [])
        const selections = choice.selections || []
        return selections.map(selection => {
            let menuItem: any = null
            for (const course of this.menu!.menuCourses) {
                menuItem = course.menuItems.find(m => m.skuId === selection.productSkuId)
                if (menuItem) break
            }
            
            const consumable = menuItem
                ? this.productLibrary!.consumables.find(c => c.id === menuItem!.consumableId)
                : this.productLibrary!.consumables.find(c => !!c.getSKUWithId(selection.productSkuId))
            const productName = consumable?.displayName || 'Item'
            const names = (selection.modifierSkuIds || [])
                .map(id => this.resolveModifierName(id))
                .filter((n): n is string => !!n)
            const modifierNames = names.length > 0 ? names.join(', ') : null
            return { productName, modifierNames }
        })
    }

    private resolveModifierName(modifierSkuId: string): string | null {
        if (!this.productLibrary) { return null }
        for (const group of (this.productLibrary.modifierGroups || [])) {
            for (const mod of group.modifiers) {
                if (mod.skus.some(s => s.id === modifierSkuId)) {
                    return mod.displayName
                }
            }
        }
        return null
    }
}
